(() => {
  const strings = window.comfortStrings || {};
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mobile = window.matchMedia('(max-width: 749px)');
  const format = (text, vars) => Object.entries(vars).reduce((s, [k, v]) => s.replaceAll(`%%${k}%%`, v), text);
  const nodes = (scope, selector) => [...scope.querySelectorAll(selector)];
  const store = {
    get(key) { try { return localStorage.getItem(key); } catch { return null; } },
    set(key, value) { try { localStorage.setItem(key, value); } catch { /* Storage can be unavailable in private browsing. */ } }
  };

  function initRails(scope = document) {
    nodes(scope, '[data-product-rail]').forEach((rail) => {
      if (rail.classList.contains('is-enhanced')) return;
      const track = rail.querySelector('[data-rail-track]');
      const cards = [...track.children];
      const previous = rail.querySelector('[data-rail-previous]');
      const next = rail.querySelector('[data-rail-next]');
      const more = rail.querySelector('[data-rail-more]');
      const count = rail.querySelector('[data-rail-count]');
      const batch = Math.max(1, Number(rail.dataset.initialCount) || 8);
      let shown = more ? Math.min(batch, cards.length) : cards.length;   // with a View more link instead of a Show more button, every card in the row is there to scroll to
      const update = () => {
        const max = track.scrollWidth - track.clientWidth;
        rail.dataset.scrollable = String(max > 2);
        previous.disabled = track.scrollLeft <= 2;
        next.disabled = track.scrollLeft >= max - 2;
      };
      const show = () => {
        cards.forEach((card, i) => { card.hidden = i >= shown; });
        if (more) more.hidden = shown >= cards.length;
        if (count) count.textContent = format(strings.railCount || 'Showing %%shown%% of %%total%%', { shown, total: cards.length });
        requestAnimationFrame(update);
      };
      const move = (direction) => track.scrollBy({left: direction * Math.max(220, track.clientWidth * .85), behavior: reducedMotion.matches ? 'auto' : 'smooth'});
      previous.addEventListener('click', () => move(-1));
      next.addEventListener('click', () => move(1));
      more?.addEventListener('click', () => {
        const firstNew = shown;
        shown = Math.min(shown + batch, cards.length);
        show();
        requestAnimationFrame(() => {
          track.scrollTo({ left: cards[firstNew].offsetLeft - track.firstElementChild.offsetLeft, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
          if (more?.hidden) track.focus({preventScroll:true});
        });
      });
      track.addEventListener('scroll', update, {passive:true});
      track.addEventListener('keydown', (event) => {
        if (event.target !== track || !['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) return;
        event.preventDefault();
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') move(event.key === 'ArrowRight' ? 1 : -1);
        else track.scrollTo({left: event.key === 'Home' ? 0 : track.scrollWidth, behavior: reducedMotion.matches ? 'auto' : 'smooth'});
      });
      const observer = new ResizeObserver(update);
      observer.observe(track);
      rail._comfortCleanup = () => observer.disconnect();
      rail.classList.add('is-enhanced');
      show();
    });
  }

  function initCarousels(scope = document) {
    nodes(scope, '[data-comfort-carousel]').forEach((carousel) => {
      if (carousel.dataset.initialized) return;
      carousel.dataset.initialized = 'true';
      const slides = nodes(carousel, '[data-comfort-slide]');
      let active = 0, timer = null, hovered = false, focused = false, pointer = null;
      let paused = reducedMotion.matches || carousel.dataset.autoplay !== 'true';
      const pause = carousel.querySelector('[data-comfort-pause]');
      const position = carousel.querySelector('[data-comfort-position]');
      const stop = () => { clearInterval(timer); timer = null; };
      const show = (index) => {
        active = (index + slides.length) % slides.length;
        slides.forEach((slide, i) => { slide.hidden = i !== active; });
        if (position) position.textContent = `${active + 1} / ${slides.length}`;
      };
      const start = () => {
        stop();
        if (!paused && !hovered && !focused && !document.hidden && !reducedMotion.matches && slides.length > 1) timer = setInterval(() => show(active + 1), Number(carousel.dataset.interval) || 9000);
      };
      const syncPause = () => {
        if (!pause) return;
        pause.setAttribute('aria-pressed', String(paused));
        pause.setAttribute('aria-label', paused ? strings.playSlideshow : strings.pauseSlideshow);
        pause.textContent = paused ? '▶' : '❚❚';
      };
      carousel.querySelector('[data-comfort-next]')?.addEventListener('click', () => { show(active + 1); start(); });
      carousel.querySelector('[data-comfort-previous]')?.addEventListener('click', () => { show(active - 1); start(); });
      pause?.addEventListener('click', () => { paused = !paused; syncPause(); start(); });
      carousel.addEventListener('mouseenter', () => { hovered = true; stop(); });
      carousel.addEventListener('mouseleave', () => { hovered = false; start(); });
      carousel.addEventListener('focusin', () => { focused = true; stop(); });
      carousel.addEventListener('focusout', (event) => { focused = carousel.contains(event.relatedTarget); start(); });
      carousel.addEventListener('pointerdown', (event) => { if (!event.target.closest('button')) pointer = {x:event.clientX,y:event.clientY}; });
      carousel.addEventListener('pointerup', (event) => {
        if (pointer && Math.abs(event.clientX-pointer.x)>50 && Math.abs(event.clientX-pointer.x)>Math.abs(event.clientY-pointer.y)) { show(active+(event.clientX<pointer.x?1:-1)); start(); }
        pointer=null;
      });
      carousel.addEventListener('pointercancel', () => { pointer=null; });
      const visibility = () => start();
      const motion = () => { if (reducedMotion.matches) paused = true; syncPause(); start(); };
      document.addEventListener('visibilitychange',visibility);
      reducedMotion.addEventListener('change',motion);
      carousel._comfortCleanup = () => { stop(); document.removeEventListener('visibilitychange',visibility); reducedMotion.removeEventListener('change',motion); };
      carousel._showSlide = (block) => { const index=slides.indexOf(block); if(index>=0) { paused=true;show(index);syncPause();stop(); } };
      syncPause();start();
    });
  }

  function initCatalog(scope = document) {
    nodes(scope,'[data-facet-browser]').forEach((browser) => {
      if(browser.classList.contains('is-enhanced')) return;
      browser.classList.add('is-enhanced');
      const panel=browser.querySelector('[data-facet-panel]');
      const overlay=browser.querySelector('.catalog-facets__overlay');
      const trigger=browser.querySelector('[data-facet-open]');
      const grid=browser.querySelector('[data-catalog-grid]');
      const view=store.get('slumbery_catalog_view') || 'grid';
      const setView=(value) => { grid?.classList.toggle('is-list',value==='list');nodes(browser,'[data-catalog-view]').forEach(b=>b.setAttribute('aria-pressed',String(b.dataset.catalogView===value))); };
      setView(view);
      const open=() => {
        if(!mobile.matches || !panel) return;
        panel.inert=false;panel.classList.add('is-open');panel.setAttribute('role','dialog');panel.setAttribute('aria-modal','true');
        trigger?.setAttribute('aria-expanded','true'); if(overlay) overlay.hidden=false;
        document.body.classList.add('facet-drawer-open');panel.querySelector('[data-facet-close]')?.focus();
      };
      const close=(restore=true) => {
        panel?.classList.remove('is-open');panel?.removeAttribute('role');panel?.removeAttribute('aria-modal');
        if(panel) panel.inert=mobile.matches && !panel.classList.contains('catalog-facets--empty');
        trigger?.setAttribute('aria-expanded','false');if(overlay) overlay.hidden=true;
        document.body.classList.remove('facet-drawer-open');if(restore) trigger?.focus();
      };
      const breakpoint=() => close(false);
      mobile.addEventListener('change',breakpoint);close(false);
      browser._comfortCleanup=() => { mobile.removeEventListener('change',breakpoint);close(false); };
      trigger?.addEventListener('click',open);
      nodes(browser,'[data-facet-close]').forEach(b=>b.addEventListener('click',()=>close()));
      panel?.addEventListener('keydown',event=>{
        if(!panel.classList.contains('is-open')) return;
        if(event.key==='Escape') {event.preventDefault();close();}
        if(event.key!=='Tab') return;
        const focusable=nodes(panel,'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),summary').filter(e=>e.getClientRects().length);
        const first=focusable[0],last=focusable.at(-1);
        if(event.shiftKey && document.activeElement===first){event.preventDefault();last.focus();}
        else if(!event.shiftKey && document.activeElement===last){event.preventDefault();first.focus();}
      });
      nodes(browser,'[data-catalog-view]').forEach(b=>b.addEventListener('click',()=>{setView(b.dataset.catalogView);store.set('slumbery_catalog_view',b.dataset.catalogView);}));
      nodes(browser,'[data-facet-value-search]').forEach(input=>input.addEventListener('input',()=>{
        const group=input.closest('.catalog-filter');const values=nodes(group,'[data-facet-value]');
        values.forEach(v=>{v.hidden=!v.dataset.valueLabel.toLocaleLowerCase().includes(input.value.trim().toLocaleLowerCase());});
        group.querySelector('[data-facet-no-options]').hidden=values.some(v=>!v.hidden);
      }));
      const form=browser.querySelector('[data-facet-form]');
      nodes(browser,'[data-price-filter]').forEach(group=>{
        const min=group.querySelector('[data-price-min]'),max=group.querySelector('[data-price-max]');
        const validate=()=>{max.setCustomValidity(min.value!=='' && max.value!=='' && Number(min.value)>Number(max.value) ? (strings.priceRangeError || 'The maximum price must be at least the minimum price.') : '');};
        min.addEventListener('input',validate);max.addEventListener('input',validate);
      });
      form?.addEventListener('submit',event=>{
        if(!form.reportValidity()) return;
        event.preventDefault();
        const url=new URL(form.action,location.origin);
        // Preserve preview/editor parameters, while resetting filters and page from form state.
        for(const [key,value] of new URL(location.href).searchParams) if(!key.startsWith('filter.') && !['q','type','options[prefix]','sort_by','page','section_id'].includes(key)) url.searchParams.append(key,value);
        for(const [key,value] of new FormData(form)) if(value!=='') url.searchParams.append(key,value);
        close(false);loadCatalog(url,browser);
      });
      browser.querySelector('[data-facet-sort]')?.addEventListener('change',()=>form?.requestSubmit());
      nodes(browser,'[data-facet-link]').forEach(link=>link.addEventListener('click',event=>{
        if(event.button!==0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();close(false);
        const target=new URL(link.href);
        // A tag link carries the price, availability and sort choices already made.
        if(link.hasAttribute('data-tag-link')) for(const [key,value] of new URL(location.href).searchParams) if(key.startsWith('filter.')||key==='sort_by') target.searchParams.set(key,value);
        loadCatalog(target,browser);
      }));
      markTagFacets(browser);
    });
  }
  // Tag filters: ask how many products each choice would leave (a tiny ?view=count request per choice) and grey out the ones that leave none.
  async function markTagFacets(browser) {
    const pills=nodes(browser,'.facet-pill:not(.is-active)').slice(0,60);
    if(!pills.length) return;
    const carry=new URLSearchParams();
    for(const [key,value] of new URL(location.href).searchParams) if(key.startsWith('filter.')) carry.append(key,value);
    let next=0;
    const worker=async() => {
      while(next<pills.length) {
        const pill=pills[next++];
        if(!pill.isConnected) return;
        try {
          const url=new URL(pill.href,location.origin);
          for(const [key,value] of carry) url.searchParams.append(key,value);
          url.searchParams.set('view','count');
          const response=await fetch(url,{credentials:'same-origin'});
          const count=parseInt((await response.text()).trim(),10);
          if(!response.ok || Number.isNaN(count) || !pill.isConnected) continue;
          pill.dataset.count=String(count);
          const none=count===0;
          pill.classList.toggle('is-unavailable',none);
          if(none) {pill.setAttribute('aria-disabled','true');pill.tabIndex=-1;} else {pill.removeAttribute('aria-disabled');pill.removeAttribute('tabindex');}
          if(!pill.querySelector('.facet-pill__count')) {const badge=document.createElement('span');badge.className='facet-pill__count';pill.append(badge);}
          pill.querySelector('.facet-pill__count').textContent=none?'':String(count);
        } catch(error) { /* a choice that cannot be counted stays as it is */ }
      }
    };
    await Promise.all([worker(),worker(),worker(),worker(),worker(),worker()]);
    // A filter whose every choice leaves nothing has nothing to offer: hide it until the other choices change.
    nodes(browser,'.catalog-filter').forEach(group=>{
      const all=nodes(group,'.facet-pill');
      if(!all.length) return;
      group.classList.toggle('is-empty',all.every(p=>p.classList.contains('is-unavailable')));
    });
  }
  document.addEventListener('click',event=>{ const pill=event.target.closest?.('.facet-pill.is-unavailable'); if(pill) {event.preventDefault();event.stopPropagation();} },true);
  let pending=null;
  async function loadCatalog(url,browser,push=true) {
    pending?.abort();const controller=new AbortController();pending=controller;
    browser.setAttribute('aria-busy','true');
    try {
      const fetchUrl=new URL(url);fetchUrl.searchParams.set('section_id',browser.dataset.sectionId);
      const response=await fetch(fetchUrl,{signal:controller.signal,credentials:'same-origin'});
      if(!response.ok) throw new Error('Results unavailable');
      const html=new DOMParser().parseFromString(await response.text(),'text/html');
      const replacement=html.querySelector(`[data-facet-browser][data-section-id="${CSS.escape(browser.dataset.sectionId)}"]`);
      if(!replacement) throw new Error('Missing results');
      browser._comfortCleanup?.();browser.replaceWith(replacement);
      if(push) history.pushState({slumberyCatalog:true},'',url);
      initCatalog(document);document.dispatchEvent(new CustomEvent('slumbery:products:updated'));
      replacement.querySelector('[data-catalog-count]')?.focus({preventScroll:true});
      const top=replacement.getBoundingClientRect().top+window.scrollY-150;
      window.scrollTo({top:Math.max(0,top),behavior:reducedMotion.matches?'auto':'smooth'});
    } catch(error) {
      if(error.name==='AbortError') return;
      // Full navigation retains native Shopify filtering when progressive enhancement fails.
      location.assign(url.href);
    } finally { browser.removeAttribute('aria-busy');if(pending===controller)pending=null; }
  }
  window.addEventListener('popstate',()=>{const browser=document.querySelector('[data-facet-browser]');if(browser) loadCatalog(new URL(location.href),browser,false);});
  function init(scope=document) {initRails(scope);initCarousels(scope);initCatalog(scope);}
  init();
  document.addEventListener('shopify:section:load',event=>init(event.target));
  document.addEventListener('shopify:section:unload',event=>nodes(event.target,'[data-product-rail],[data-comfort-carousel],[data-facet-browser]').forEach(el=>el._comfortCleanup?.()));
  document.addEventListener('shopify:block:select',event=>event.target.closest('[data-comfort-carousel]')?._showSlide?.(event.target.closest('[data-comfort-slide]')));
  document.addEventListener('slumbery:products:updated',()=>initRails());
})();
